import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import Papa from 'papaparse';

interface FieldChangeEvent {
  fieldKey: string;
  value: string;
}

interface TemperatureChangeEvent {
  readingId: number;
  value: string;
}

export interface HphtRheologyReadingView {
  id: number;
  temperature: number;
}

export interface HphtRheologyRowView {
  key: string;
  label: string;
  type: 'number';
  min?: number;
  step?: number;
  tttm?: boolean;
}

export interface Fann35ReferenceView {
  requestedTemperature: string;
  requestedPressure: string;
  calculated600Rpm: string;
  rowValues: Record<string, string>;
  plasticViscosity: string;
  yieldPoint: string;
}

interface RawFann77Reading {
  elapsedTime: number;
  temperature: number;
  rpm: number;
  pressure: number;
  fannDegrees: number;
}

interface RawFann77Columns {
  elapsedTime: number;
  temperature: number;
  rpm: number;
  pressure: number;
  fannDegrees: number;
}

const RAW_FANN77_FIELD_KEYS = [
  'rpm600',
  'rpm300',
  'rpm200',
  'rpm100',
  'rpm60',
  'rpm30',
  'rpm20',
  'rpm10',
  'rpm6',
  'rpm3',
  'gel10s600',
  'gel10s300',
  'gel10s',
  'gel10m600',
  'gel10m',
  'end600'
] as const;

const RAW_FANN77_STAGES: ReadonlyArray<{
  rpms: number[];
  selection: 'lowest' | 'highest' | 'last';
}> = [
  { rpms: [599, 600, 601], selection: 'lowest' },
  { rpms: [299, 300, 301], selection: 'highest' },
  { rpms: [200], selection: 'last' },
  { rpms: [100], selection: 'last' },
  { rpms: [60], selection: 'last' },
  { rpms: [30], selection: 'last' },
  { rpms: [20], selection: 'last' },
  { rpms: [10], selection: 'last' },
  { rpms: [6], selection: 'last' },
  { rpms: [3], selection: 'last' },
  { rpms: [599, 600, 601], selection: 'lowest' },
  { rpms: [299, 300, 301], selection: 'highest' },
  { rpms: [1, 2, 3, 4], selection: 'highest' },
  { rpms: [599, 600, 601], selection: 'lowest' },
  { rpms: [1, 2, 3, 4], selection: 'highest' },
  { rpms: [599, 600, 601], selection: 'lowest' }
];

// Ordered most-specific-first so substring matching picks the right row for CSV import.
const CSV_LABEL_TO_FIELD_KEY: ReadonlyArray<{ label: string; fieldKey: string }> = [
  { label: 'requested pressure', fieldKey: 'pressure' },
  { label: 'diff. pressure', fieldKey: 'pressure' },
  { label: '600 rpm (10 sec)', fieldKey: 'gel10s600' },
  { label: '300 rpm (10 sec)', fieldKey: 'gel10s300' },
  { label: '10 second gel', fieldKey: 'gel10s' },
  { label: '600 rpm (10 min)', fieldKey: 'gel10m600' },
  { label: '10 minute gel', fieldKey: 'gel10m' },
  { label: '600 rpm (end)', fieldKey: 'end600' },
  { label: '600 rpm', fieldKey: 'rpm600' },
  { label: '300 rpm', fieldKey: 'rpm300' },
  { label: '200 rpm', fieldKey: 'rpm200' },
  { label: '100 rpm', fieldKey: 'rpm100' },
  { label: '60 rpm', fieldKey: 'rpm60' },
  { label: '30 rpm', fieldKey: 'rpm30' },
  { label: '20 rpm', fieldKey: 'rpm20' },
  { label: '10 rpm', fieldKey: 'rpm10' },
  { label: '6 rpm', fieldKey: 'rpm6' },
  { label: '3 rpm', fieldKey: 'rpm3' }
];

@Component({
  selector: 'app-job-request-test-hpht-rheology-detail',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  templateUrl: './job-request-test-hpht-rheology-detail.component.html',
  styleUrl: './job-request-test-hpht-rheology-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobRequestTestHphtRheologyDetailComponent {
  @Input({ required: true }) testKey = '';
  @Input() temperatures: HphtRheologyReadingView[] = [];
  @Input() hphtRheologyRows: HphtRheologyRowView[] = [];
  @Input({ required: true }) gridTemplateColumns = '';
  @Input({ required: true }) fieldValueByKey!: (testKey: string, fieldKey: string) => string;
  @Input({ required: true }) hphtRheologyFieldKey!: (readingId: number, rowKey: string) => string;
  @Input({ required: true }) calculated600Rpm!: (testKey: string, readingId: number) => string;
  @Input({ required: true }) plasticViscosity!: (testKey: string, readingId: number) => string;
  @Input({ required: true }) yieldPoint!: (testKey: string, readingId: number) => string;
  @Input() fann35Column: Fann35ReferenceView | null = null;

  @Output() fieldChanged = new EventEmitter<FieldChangeEvent>();
  @Output() addReading = new EventEmitter<void>();
  @Output() removeReading = new EventEmitter<number>();
  @Output() temperatureChanged = new EventEmitter<TemperatureChangeEvent>();

  protected importMessage = '';
  protected importError = '';

  protected onFieldInput(fieldKey: string, value: string): void {
    this.fieldChanged.emit({ fieldKey, value: this.wholeNumber(value) });
  }

  protected onTemperatureChange(readingId: number, value: string): void {
    this.temperatureChanged.emit({ readingId, value: this.wholeNumber(value) });
  }

  protected wholeNumber(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? String(Math.round(numericValue)) : String(value);
  }

  // Rows above the Plastic Viscosity / Yield Point pair.
  protected viscosityRows(): HphtRheologyRowView[] {
    return this.hphtRheologyRows.slice(0, 10);
  }

  // Rows below the Plastic Viscosity / Yield Point pair.
  protected gelRows(): HphtRheologyRowView[] {
    return this.hphtRheologyRows.slice(10);
  }

  protected isTttm(readingId: number, row: HphtRheologyRowView): boolean {
    if (!row.tttm) {
      return false;
    }

    const rawValue = this.fieldValueByKey(this.testKey, this.hphtRheologyFieldKey(readingId, row.key)).trim();
    if (!rawValue) {
      return false;
    }

    const value = Number(rawValue);
    return Number.isFinite(value) && value >= 310;
  }

  protected openCsvFilePicker(event: Event, fileInput: HTMLInputElement): void {
    event.preventDefault();
    fileInput.click();
  }

  protected async onCsvSelected(event: Event): Promise<void> {
    this.importError = '';
    this.importMessage = '';

    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }

    try {
      const text = await file.text();
      const rows = (Papa.parse<string[]>(text, { skipEmptyLines: true }).data ?? []) as string[][];
      const updatedCount = this.applyImportedRows(rows);

      if (updatedCount <= 0) {
        this.importError = 'No FANN 77 values were imported. Check the CSV rows/columns and try again.';
      } else {
        this.importMessage = `Imported ${updatedCount} value${updatedCount === 1 ? '' : 's'} from ${file.name}.`;
      }
    } catch {
      this.importError = 'Failed to parse CSV file.';
    } finally {
      if (input) {
        input.value = '';
      }
    }
  }

  private applyImportedRows(rows: string[][]): number {
    const headerIndex = rows.findIndex(row => this.normalizeCsvHeader(row[0] ?? '') === 'elapsedtime');
    if (headerIndex >= 0) {
      return this.applyRawImportedRows(rows, headerIndex);
    }

    return this.applyLabeledImportedRows(rows);
  }

  private applyRawImportedRows(rows: string[][], headerIndex: number): number {
    let updated = 0;

    const headers = rows[headerIndex] ?? [];
    const columns: RawFann77Columns = {
      elapsedTime: this.findCsvColumn(headers, ['elapsedtime']),
      temperature: this.findCsvColumn(headers, ['sampletempf', 'temperature']),
      rpm: this.findCsvColumn(headers, ['rpm']),
      pressure: this.findCsvColumn(headers, ['pressure']),
      fannDegrees: this.findCsvColumn(headers, ['fanndegrees'])
    };

    if (Object.values(columns).some(column => column < 0)) {
      return updated;
    }

    const readings = rows
      .slice(headerIndex + 1)
      .map(row => this.parseRawFann77Reading(row, columns))
      .filter((reading): reading is RawFann77Reading => reading !== null);
    const buckets = this.bucketRawFann77Readings(readings);

    for (let bucketIndex = 0; bucketIndex < Math.min(buckets.length, this.temperatures.length); bucketIndex++) {
      const reading = this.temperatures[bucketIndex];
      const values = buckets[bucketIndex];

      for (let valueIndex = 0; valueIndex < values.length; valueIndex++) {
        const value = values[valueIndex];
        if (value === null) {
          continue;
        }

        this.onFieldInput(
          this.hphtRheologyFieldKey(reading.id, RAW_FANN77_FIELD_KEYS[valueIndex]),
          String(value)
        );
        updated++;
      }
    }

    return updated;
  }

  private applyLabeledImportedRows(rows: string[][]): number {
    let updated = 0;

    for (const row of rows) {
      const label = (row[0] ?? '').trim().toLowerCase();
      if (!label) {
        continue;
      }

      const mapping = CSV_LABEL_TO_FIELD_KEY.find(entry => label.includes(entry.label));
      if (!mapping) {
        continue;
      }

      for (let column = 1; column < row.length; column++) {
        const reading = this.temperatures[column - 1];
        const cellValue = (row[column] ?? '').trim();
        if (reading === undefined || !cellValue) {
          continue;
        }

        this.onFieldInput(this.hphtRheologyFieldKey(reading.id, mapping.fieldKey), cellValue);
        updated++;
      }
    }

    return updated;
  }

  private normalizeCsvHeader(value: string): string {
    return value.replace(/^\uFEFF/, '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private findCsvColumn(headers: string[], names: string[]): number {
    const normalizedNames = new Set(names);
    return headers.findIndex(header => normalizedNames.has(this.normalizeCsvHeader(header)));
  }

  private parseRawFann77Reading(row: string[], columns: RawFann77Columns): RawFann77Reading | null {
    const values = [
      Number((row[columns.elapsedTime] ?? '').trim()),
      Number((row[columns.temperature] ?? '').trim()),
      Number((row[columns.rpm] ?? '').trim()),
      Number((row[columns.pressure] ?? '').trim()),
      Number((row[columns.fannDegrees] ?? '').trim())
    ];

    if (values.some(value => !Number.isFinite(value))) {
      return null;
    }

    return {
      elapsedTime: values[0],
      temperature: Math.round(values[1]),
      rpm: Math.round(values[2]),
      pressure: Math.round(values[3]),
      fannDegrees: values[4]
    };
  }

  private bucketRawFann77Readings(readings: RawFann77Reading[]): Array<Array<number | null>> {
    const buckets: Array<Array<number | null>> = [];
    let readingIndex = 0;

    while (readingIndex < readings.length) {
      const bucket: Array<number | null> = [];
      let hasValue = false;

      for (const stage of RAW_FANN77_STAGES) {
        const selected = this.takeRawFann77Stage(readings, readingIndex, stage.rpms, stage.selection);
        bucket.push(selected.value);
        readingIndex = selected.nextIndex;
        hasValue ||= selected.value !== null;

        if (selected.value === null) {
          break;
        }
      }

      if (hasValue) {
        buckets.push(bucket);
      }

      if (readingIndex >= readings.length || !hasValue) {
        break;
      }
    }

    return buckets;
  }

  private takeRawFann77Stage(
    readings: RawFann77Reading[],
    startIndex: number,
    rpms: number[],
    selection: 'lowest' | 'highest' | 'last'
  ): { value: number | null; nextIndex: number } {
    const acceptedRpms = new Set(rpms);
    let firstIndex = startIndex;
    while (firstIndex < readings.length && !acceptedRpms.has(readings[firstIndex].rpm)) {
      firstIndex++;
    }

    if (firstIndex >= readings.length) {
      return { value: null, nextIndex: readings.length };
    }

    let nextIndex = firstIndex;
    while (nextIndex < readings.length && acceptedRpms.has(readings[nextIndex].rpm)) {
      nextIndex++;
    }

    const group = readings.slice(firstIndex, nextIndex);
    let selectedReading = group[0];
    if (selection === 'lowest') {
      selectedReading = group.reduce((lowest, reading) =>
        reading.fannDegrees < lowest.fannDegrees ? reading : lowest
      );
    } else if (selection === 'highest') {
      selectedReading = group.reduce((highest, reading) =>
        reading.fannDegrees > highest.fannDegrees ? reading : highest
      );
    } else {
      selectedReading = group[group.length - 1];
    }

    return { value: selectedReading.fannDegrees, nextIndex };
  }
}
